import { useEffect } from 'react'
import { wsClient } from '../ws/client'
import { useChatStore } from '../stores/chatStore'
import { useAuthStore } from '../stores/authStore'
import { useUiStore } from '../stores/uiStore'
import { chatsApi } from '../api/chats'
import type {
  WsEditedPayload,
  WsDeletedPayload,
  WsMsgPayload,
  WsTypingPayload,
  WsReadPayload,
  WsReactionPayload,
  Message,
} from '../types'

// Хранилище активных уведомлений chatId → Notification
const pendingNotifs = new Map<string, Notification>()

function playNotifSound() {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.3)
  } catch { /* AudioContext blocked */ }
}

function closeNotif(chatId: string) {
  const n = pendingNotifs.get(chatId)
  if (n) {
    n.close()
    pendingNotifs.delete(chatId)
  }
  // Also close any SW-displayed push notifications for this chat
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLOSE_NOTIFICATION', chatId })
  }
}

function showNotif(chatId: string, title: string, body: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  const n = new Notification(title, { body, tag: chatId, icon: '/icon.svg' })
  pendingNotifs.set(chatId, n)
  n.onclick = () => {
    window.focus()
    n.close()
    pendingNotifs.delete(chatId)
  }
}

export function useWebSocket(token: string | null) {
  const activeChatId = useChatStore((s) => s.activeChatId)

  // Запрашиваем разрешение на уведомления при загрузке
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Закрываем уведомление при открытии чата
  useEffect(() => {
    if (activeChatId) closeNotif(activeChatId)
  }, [activeChatId])

  useEffect(() => {
    if (!token) return

    wsClient.connect(token)

    const off = wsClient.on((event) => {
      // Всегда берём свежее состояние стора, чтобы избежать stale closure
      const s = useChatStore.getState()
      switch (event.type) {
        case 'message.new': {
          const p = event.payload as WsMsgPayload
          const msg: Message = {
            id: p.id,
            chat_id: p.chat_id,
            sender_id: p.sender_id,
            text: p.text,
            media_ids: p.media_ids,
            reply_to_message_id: p.reply_to_message_id,
            forward_from_chat_id: p.fw_from_chat_id,
            forward_from_msg_id: p.fw_from_msg_id,
            deleted_for_all: false,
            ttl_seconds: 0,
            created_at: new Date(p.created_at * 1000).toISOString(),
            edited_at: null,
          }
          s.appendMessage(msg)
          s.updateLastMessage(p.chat_id, msg)

          // Если чат новый — подтягиваем только его и добавляем через upsert,
          // не заменяя весь список (иначе теряются уже обогащённые peer/title).
          if (!s.chats.some((c) => c.id === p.chat_id)) {
            chatsApi.list().then((res) => {
              const newChat = (res.chats ?? []).find((c) => c.id === p.chat_id)
              if (newChat) useChatStore.getState().upsertChat(newChat)
            })
          }

          const currentUserId = useAuthStore.getState().userId
          // Badge: только для чужих сообщений в не-активном чате.
          // Если чат открыт — MessageList видит сообщение напрямую; badge не нужен
          // (и сразу же сбрасывался бы через resetUnread, вызывая мигание).
          if (p.sender_id !== currentUserId && s.activeChatId !== p.chat_id) {
            s.incrementUnread(p.chat_id)
          }

          // Системное уведомление + звук — только если чат не открыт прямо сейчас
          if (s.activeChatId !== p.chat_id && p.sender_id !== currentUserId) {
            const chat = s.chats.find((c) => c.id === p.chat_id)
            const title = chat?.title ?? chat?.peer?.first_name ?? 'New message'
            showNotif(p.chat_id, title, p.text || '📎 Media')
            if (useUiStore.getState().soundEnabled) {
              playNotifSound()
            }
          }
          break
        }
        case 'message.edited': {
          const p = event.payload as WsEditedPayload
          s.updateMessage(p.chat_id, p.message_id, {
            text: p.new_text,
            edited_at: new Date(p.edited_at * 1000).toISOString(),
          })
          break
        }
        case 'message.deleted': {
          const p = event.payload as WsDeletedPayload
          s.removeMessage(p.chat_id, p.message_id)
          break
        }
        case 'typing.start': {
          const p = event.payload as WsTypingPayload
          s.setTyping(p.chat_id, p.user_id)
          setTimeout(() => useChatStore.getState().clearTyping(p.chat_id, p.user_id), 6000)
          break
        }
        case 'typing.stop': {
          const p = event.payload as WsTypingPayload
          s.clearTyping(p.chat_id, p.user_id)
          break
        }
        case 'read.update': {
          const p = event.payload as WsReadPayload
          const currentUserId = useAuthStore.getState().userId
          if (p.user_id === currentUserId) {
            s.resetUnread(p.chat_id)
            closeNotif(p.chat_id)
          }
          // Update read_by on messages up to last_read_message_id
          const msgs = useChatStore.getState().messages[p.chat_id] ?? []
          msgs.forEach((m) => {
            if (m.id <= p.last_read_message_id && !(m.read_by ?? []).includes(p.user_id)) {
              useChatStore.getState().updateMessage(p.chat_id, m.id, {
                read_by: [...(m.read_by ?? []), p.user_id],
              })
            }
          })
          break
        }
        case 'reaction.update': {
          const p = event.payload as WsReactionPayload
          s.updateMessage(p.chat_id, p.message_id, { reactions: p.reactions })
          break
        }
      }
    })

    return () => {
      off()
      wsClient.disconnect()
    }
  }, [token]) // только token — store читаем через getState() внутри handlers
}
